import { ConfirmationDialog } from "apps/web/src/componentLibrary";
import { useSnackbar } from "apps/web/src/components/Snackbar/SnackbarContext";
import TablePage from "apps/web/src/components/universal/TablePage";
import {
  CreateBulkAssignGoalTemplateJobInput,
  GoalConfigurationTemplateTableItemFragment,
  useAssignGoalConfigurationTemplateToAthleteMutation,
  useCreateGoalConfigurationTemplateMutation,
  useDeleteGoalConfigurationTemplateMutation,
  useEditGoalConfigurationTemplateMutation,
  useGoalConfigurationTemplateOffsetConnectionQuery
} from "apps/web/src/types";
import { CreateGoalConfigurationTemplateDialog } from "apps/web/src/views/GoalConfigurationTemplates/Dialogs/CreateGoalConfigurationTemplateDialog";
import { useState } from "react";
import AssignGoalConfigFromTemplateDialog from "./Dialogs/AssignGoalConfigFromTemplateDialog";
import ViewAssignedAthletesDialog from "./Dialogs/ViewAssignedAthletesDialog";
import { useGoalConfigurationTemplatePageContext } from "./GoalConfigurationTemplateProvider";
import { NoResults } from "./Table/NoResults";
import { TableBodyRows } from "./Table/TableBodyRows";
import { TableHeaderRow } from "./Table/TableHeaderRow";
import { TablePageHeader } from "./Table/TablePageHeader";
import { CreateGoalConfigurationTemplateDialogMode, GoalConfigurationTemplateFormType, buildCreateGoalInput, buildEditGoalInput } from "./utils";
import { useNavigate } from "react-router-dom-v5-compat";
import { NAV_ORG_TEAM_MANAGEMENT_GOALS_BULK_ASSIGNMENT_STATUS } from "apps/web/src/pages/Auth/Org/TeamManagement/TeamManagementPaths";

type GoalConfigurationTemplateContentProps = {
    tabs: string[];
    selectedTab: string;
    setSelectedTab: (tab: string) => void;
};

export const GoalConfigurationTemplateContent = ({ tabs, selectedTab, setSelectedTab }: GoalConfigurationTemplateContentProps) => {
    const navigate = useNavigate();
    const { setMessage } = useSnackbar();
    const { category, target, createdByMe, paginationHooks } = useGoalConfigurationTemplatePageContext();
    const { limit, offset, queryText } = paginationHooks;

    const [deleteGoal, setDeleteGoal] = useState<GoalConfigurationTemplateTableItemFragment | null>(null);
    const [selectedGoal, setSelectedGoal] = useState<GoalConfigurationTemplateTableItemFragment | null>(null);
    const [dialogMode, setDialogMode] = useState<CreateGoalConfigurationTemplateDialogMode | null>(null);
    const [loading, setLoading] = useState(false);
    const [openViewAssignedAthletes, setOpenViewAssignedAthletes] = useState(false);
    const [openAssignGoalConfigFromTemplate, setOpenAssignGoalConfigFromTemplate] = useState(false);

    const [createGoalConfigurationTemplate] = useCreateGoalConfigurationTemplateMutation({
        update: cache => {
            cache.evict({ id: "ROOT_QUERY", fieldName: "goalConfigurationTemplatesOffsetConnection" });
            cache.gc();
        },
    });
    const [deleteGoalConfigurationTemplate] = useDeleteGoalConfigurationTemplateMutation();
    const [editGoalConfigurationTemplate] = useEditGoalConfigurationTemplateMutation();
    const [assignGoalConfigurationTemplateToAthlete] = useAssignGoalConfigurationTemplateToAthleteMutation();

    const filterOptions = {
        searchText: queryText || null,
        target: target.id === "progress" ? true : target.id === "complete" ? false : null,
        uploadedByMe: createdByMe || null,
        categoryId: category.id !== "none" ? category.id : null,
    };

    const {
        data,
        loading: loadingFetchGoalConfigTemplates,
        refetch
    } = useGoalConfigurationTemplateOffsetConnectionQuery({
        variables: { pagination: { offset, limit }, filterOptions },
        fetchPolicy: 'cache-and-network'
    });

    const tableData = data?.goalConfigurationTemplatesOffsetConnection.edges || [];

    const noGoalFound = !tableData.length &&
        !filterOptions.searchText &&
        !filterOptions.categoryId &&
        filterOptions.target === null &&
        !filterOptions.uploadedByMe &&
        !loadingFetchGoalConfigTemplates;

    const handleDeleteGoal = async () => {
        if (!deleteGoal) return;

        await deleteGoalConfigurationTemplate({
            variables: {
                input: {
                    id: deleteGoal.id
                }
            }
        });

        refetch();
        setMessage("success", `${deleteGoal.name} has been deleted`);
        setDeleteGoal(null);
    };

    const handleEditGoal = async (goal: GoalConfigurationTemplateFormType) => {
        if (!selectedGoal) return;

        await editGoalConfigurationTemplate({
            variables: {
                input: buildEditGoalInput(goal, selectedGoal)
            }
        });

        refetch();
        closeCreateGoalDialog();
        setMessage("success", `${goal.task} has been edited`);
    };

    const handleCreateGoal = async (goal: GoalConfigurationTemplateFormType) => {
        await createGoalConfigurationTemplate({
            variables: {
                input: buildCreateGoalInput(goal)
            }
        });

        refetch();
        closeCreateGoalDialog();
        setMessage("success", `${goal.task} has been created as a new goal`);
    };

    const handleAssignAthletes = async (input: CreateBulkAssignGoalTemplateJobInput) => {
        setLoading(true);

        try {
            const response = await assignGoalConfigurationTemplateToAthlete({
                variables: { input },
            });
            const { data, errors } = response;
            // Check for GraphQL errors in the response
            if (errors && errors.length > 0) {
                const errorMessage = errors[0]?.message || "Something went wrong. Please try again in a few minutes.";
                setMessage("error", errorMessage);
            } else {
                const athleteCount = data?.createBulkAssignGoalTemplateJob.athleteCount || 0;
                setMessage("success", `${selectedGoal?.name} assignment has been started for ${athleteCount} athletes`);
                navigate(NAV_ORG_TEAM_MANAGEMENT_GOALS_BULK_ASSIGNMENT_STATUS);
            }
        } catch (error: any) {
            // Apollo throws GraphQL errors by default even when 200 response code is returned
            const errorMessage = error?.message || "Something went wrong. Please try again in a few minutes.";
            setMessage("error", errorMessage);
        } finally {
            setLoading(false);
            setSelectedGoal(null);
        }
    };

    const closeCreateGoalDialog = () => {
        setDialogMode(null);
        setSelectedGoal(null);
    };

    const openCreateGoalDialog = () => {
        setDialogMode(CreateGoalConfigurationTemplateDialogMode.Create);
        setSelectedGoal(null);
    };

    const handleDuplicateGoal = (goal: GoalConfigurationTemplateTableItemFragment) => {
        setDialogMode(CreateGoalConfigurationTemplateDialogMode.Duplicate);
        setSelectedGoal(goal);
    };

    const handleEditGoalClick = (goal: GoalConfigurationTemplateTableItemFragment) => {
        setDialogMode(goal.assignedAthleteCount > 0 ? CreateGoalConfigurationTemplateDialogMode.View : CreateGoalConfigurationTemplateDialogMode.Edit);
        setSelectedGoal(goal);
    };

    const handleViewAssignedAthletes = (goal: GoalConfigurationTemplateTableItemFragment) => {
        setSelectedGoal(goal);
        setOpenViewAssignedAthletes(true);
    };

    const handleAssignGoal = (goal: GoalConfigurationTemplateTableItemFragment) => {
        setSelectedGoal(goal);
        setOpenAssignGoalConfigFromTemplate(true);
    };

    return (
        <>
            <TablePage
                header={<TablePageHeader onCreate={openCreateGoalDialog} tabs={tabs} selectedTab={selectedTab} setSelectedTab={setSelectedTab} hasData={!noGoalFound}/>}
                tableHeaderRow={!noGoalFound && <TableHeaderRow />}
                paginationHooks={paginationHooks}
                tableBodyRows={noGoalFound ? <NoResults /> : tableData.map((data) => (
                    <TableBodyRows
                        key={data.id}
                        data={data}
                        onDelete={() => setDeleteGoal(data)}
                        onDuplicate={() => handleDuplicateGoal(data)}
                        onClick={() => handleEditGoalClick(data)}
                        onViewAssignedAthlete={() => handleViewAssignedAthletes(data)}
                        onAssignGoal={() => handleAssignGoal(data)}
                    />
                ))}
                loading={loadingFetchGoalConfigTemplates || loading}
                total={data?.goalConfigurationTemplatesOffsetConnection.pageInfo.total || 0}
            />

            {dialogMode !== null && (
                <CreateGoalConfigurationTemplateDialog
                    mode={dialogMode}
                    open={dialogMode !== null}
                    onClose={closeCreateGoalDialog}
                    onSubmit={(goal) => dialogMode === CreateGoalConfigurationTemplateDialogMode.Edit ? handleEditGoal(goal) : handleCreateGoal(goal)}
                    selectedGoalConfigurationTemplate={selectedGoal}
                />
            )}

            <ConfirmationDialog
                open={deleteGoal !== null}
                title="Delete Goal"
                message="Are you sure you want to delete the goal? Doing so will remove it from this list, but not from the individual athlete(s) this has been assigned to."
                onCancel={() => setDeleteGoal(null)}
                onConfirm={handleDeleteGoal}
                variant="containedDestructive"
                confirmLabel="Yes"
            />

            {selectedGoal && openViewAssignedAthletes && (
                <ViewAssignedAthletesDialog
                    open={openViewAssignedAthletes}
                    onClose={() => setOpenViewAssignedAthletes(false)}
                    goalConfigTemplate={selectedGoal}
                />
            )}

            {selectedGoal && openAssignGoalConfigFromTemplate && (
                <AssignGoalConfigFromTemplateDialog
                    goalConfigTemplate={selectedGoal}
                    open={openAssignGoalConfigFromTemplate}
                    onClose={() => setOpenAssignGoalConfigFromTemplate(false)}
                    onSubmit={handleAssignAthletes}
                />
            )}
        </>
    );
};
